// Builders and parsers for the strict, coordinate-style named-block schema.
//
//   Groups:       Pool_[A-Z]+ (base-26)              e.g. Pool_A, Pool_AA
//   Matrix cells: Cell_Pool[ID]_[RowPID]_[ColPID]    e.g. Cell_PoolA_P1_P3
//   Bracket:      Node_R[RoundIndex]_M[MatchIndex]   e.g. Node_R1_M1
//   Slots:        Slot_Pool[ID]_[1st|2nd]            e.g. Slot_PoolA_1st
//
// Note the asymmetry from the spec: a group id keeps the underscore (`Pool_A`)
// while cell/slot ids embed the pool as `PoolA` (no underscore).

import type { PlayerId, PlayerKey } from './types';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * 0 -> "A", 1 -> "B", ... 25 -> "Z", 26 -> "AA", 27 -> "AB", ...
 *
 * Bijective base-26 (spreadsheet-column style) so any number of groups gets a
 * clean, collision-free label. Groups past Z occur once a field is large enough
 * to need more than 26 pools.
 */
export function poolLabel(index: number): string {
  let n = index;
  let label = '';
  do {
    label = LETTERS[n % 26] + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
}

/** "A" -> "Pool_A" */
export function poolId(label: string): string {
  return `Pool_${label}`;
}

/** 1 -> "P1" */
export function playerId(index: number): PlayerId {
  return `P${index}`;
}

/** "Player 1" display name from a 1-based index. */
export function playerName(index: number): string {
  return `Player ${index}`;
}

/** Stable cross-tournament player key from a unique token, e.g. "PLR_<uuid>". */
export function playerKey(token: string): PlayerKey {
  return `PLR_${token}`;
}

/** Stable tournament id from a unique token, e.g. "Tournament_<uuid>". */
export function tournamentEntityId(token: string): string {
  return `Tournament_${token}`;
}

/** Stable registration id from a unique token, e.g. "Reg_<uuid>". */
export function registrationEntityId(token: string): string {
  return `Reg_${token}`;
}

/** Stable tournament-photo id from a unique token, e.g. "Photo_<uuid>". */
export function photoEntityId(token: string): string {
  return `Photo_${token}`;
}

/**
 * Opaque capability token for an athlete's shareable profile page, e.g.
 * "Profile_<uuid>". Built from an unguessable token (a UUID) so "anyone with
 * the link" can open the profile without an account — the same unlisted-link
 * model as the tournament share view. Deliberately distinct from the athlete's
 * personal number (the platform primary key), which must never appear on a
 * public URL.
 */
export function athleteProfileToken(token: string): string {
  return `Profile_${token}`;
}

/**
 * Opaque capability token for a sport club's shareable public home page, e.g.
 * "Club_<uuid>". Built from an unguessable token (a UUID) so "anyone with the
 * link" can open the club's home page without an account — the same unlisted-link
 * model as the tournament share view and the athlete profile page. Deliberately
 * distinct from the company's integer id, which is small and guessable and must
 * never be the capability on a public URL.
 */
export function clubHomeToken(token: string): string {
  return `Club_${token}`;
}

/**
 * Stable id of an athlete-access grant, e.g. "Access_<uuid>" (see
 * `features/athlete-access.md`). An opaque row id, not a capability: the grant
 * is addressed by it on the revoke routes, which authorise off the caller's own
 * links rather than off knowledge of the id.
 */
export function athleteAccessId(token: string): string {
  return `Access_${token}`;
}

/**
 * Stable id of a pending athlete-access invitation, e.g. "Invite_<uuid>".
 * Deliberately distinct from the emailed claim token, whose keyed hash is the
 * only secret material the row stores — exactly the `Login_<uuid>` split.
 */
export function athleteInviteId(token: string): string {
  return `Invite_${token}`;
}

/**
 * Id of an in-progress company sign-up, e.g. "Signup_<uuid>" (see
 * `features/company-registration.md`). Doubles as the wizard's capability:
 * built from an unguessable token (a UUID) and handed only to the browser that
 * started the sign-up, it addresses the later wizard steps — which stay inert
 * until the emailed passcode proves the registrant controls the address.
 */
export function companySignupId(token: string): string {
  return `Signup_${token}`;
}

/**
 * Referee_[n] from a 1-based index, e.g. "Referee_1". Unlike player ids these
 * are never renumbered — a removed referee leaves a gap so surviving match
 * assignments keep pointing at the right person.
 */
export function refereeId(index: number): string {
  return `Referee_${index}`;
}

/** Parse "Referee_3" -> 3, or null if malformed. */
export function parseRefereeId(id: string): number | null {
  const match = /^Referee_(\d+)$/.exec(id);
  return match ? Number(match[1]) : null;
}

/** Whether an id addresses a group-stage match cell ("Cell_..."). */
export function isCellId(id: string): boolean {
  return id.startsWith('Cell_');
}

/** Whether an id addresses a playoff bracket node ("Node_..."). */
export function isNodeId(id: string): boolean {
  return id.startsWith('Node_');
}

/** Cell_Pool[Label]_[RowPID]_[ColPID] */
export function cellId(label: string, rowPlayer: PlayerId, colPlayer: PlayerId): string {
  return `Cell_Pool${label}_${rowPlayer}_${colPlayer}`;
}

/** Node_R[round]_M[matchIndex] */
export function nodeId(round: number, matchIndex: number): string {
  return `Node_R${round}_M${matchIndex}`;
}

/** Slot_Pool[Label]_[1st|2nd] */
export function slotId(label: string, position: 1 | 2): string {
  return `Slot_Pool${label}_${position === 1 ? '1st' : '2nd'}`;
}

/** Time_Slot_[n] label used by the scheduler view. */
export function timeSlotLabel(timeSlot: number): string {
  return `Time_Slot_${timeSlot}`;
}

export interface ParsedNodeId {
  round: number;
  matchIndex: number;
}

/** Parse "Node_R1_M2" -> { round: 1, matchIndex: 2 }, or null if malformed. */
export function parseNodeId(id: string): ParsedNodeId | null {
  const match = /^Node_R(\d+)_M(\d+)$/.exec(id);
  if (!match) return null;
  return { round: Number(match[1]), matchIndex: Number(match[2]) };
}

/** Parse "PLR_<token>" -> "<token>", or null if malformed. */
export function parsePlayerKey(key: string): string | null {
  const match = /^PLR_(.+)$/.exec(key);
  return match ? match[1] : null;
}
